import type { License } from 'common-billing-server/types'
import { formatDateTime } from '../../format-date'

export type LicenseStatus =
    | { state: 'valid' }
    | { state: 'invalidated'; reason: string; at: Date }
    | { state: 'expired'; at: Date }

type LicenseStatusFields = Pick<License, 'invalidatedAt' | 'invalidationReason' | 'entitlements'>

/**
 * Derives the status shown for a license. An explicit invalidation (revocation) takes precedence over an
 * entitlement expiry. Expiry is judged from `entitlements.expiresAt` for every product: after that instant
 * the billing server refuses installs and upgrades, and the installation itself serves its appliances an
 * empty configuration.
 */
export function getLicenseStatus(license: LicenseStatusFields, now: Date = new Date()): LicenseStatus {
    if (license.invalidatedAt) {
        return { state: 'invalidated', reason: license.invalidationReason ?? 'invalidated', at: license.invalidatedAt }
    }
    const expiresAt = license.entitlements?.expiresAt
    if (expiresAt && !isNaN(expiresAt.getTime()) && expiresAt.getTime() < now.getTime()) {
        return { state: 'expired', at: expiresAt }
    }
    return { state: 'valid' }
}

/** Lower-case status text such as 'expired at 2026-09-21 23:59 (UTC)'; pages capitalise to match their own convention. */
export function formatLicenseStatus(status: LicenseStatus): string {
    switch (status.state) {
        case 'valid':
            return 'valid'
        case 'invalidated':
            return `${status.reason} at ${formatDateTime(status.at)} (UTC)`
        case 'expired':
            return `expired at ${formatDateTime(status.at)} (UTC)`
    }
}
